<template>
  <div v-if="showContent">
    <HeadingDisplay
      title="Envie um comprovante de residência atualizado"
      head="Comprovante de residência"
    />
    <BaseText>
      Para seguir com a atualização do seu cadastro, precisamos de um
      comprovante de residência recente (até 90 dias) em seu nome, com os dados
      legíveis e o endereço completo.
    </BaseText>
    <BaseText>
      Exemplos: conta de luz, água, telefone, contrato de aluguel, fatura do
      cartão, entre outros.
    </BaseText>

    <div v-if="fileName || imageUrl" class="preview-container">
      <ButtonIconPrimary
        class="preview-container__close-button"
        label="remover"
        icon="EF0030"
        data-testid="close-button"
        @click="removeSelectedFile"
      />
      <div v-if="fileName" class="preview-container-file">
        <div class="preview-container-file__header">
          <InitialAvatarIcon icon="ED0010" />
          <ChipStatus label="Adicionado" status="positive" />
        </div>
        <span class="preview-container-file__span">
          {{ fileName }}
        </span>
      </div>
      <img
        v-else-if="imageUrl"
        alt="Documento de identificação"
        class="preview-container-image"
        :src="imageUrl"
        style="width: 100%"
      />
    </div>

    <div v-else class="upload-area" @click="uploadDocument">
      <BaseIcon
        icon="EA0230"
        class="upload-area__icon"
        :color="NebraskaColors.elementPrimary"
        size="large"
      />
      <BaseText size="p6"> Clique para adicionar o comprovante </BaseText>
    </div>

    <input
      ref="fileInputRef"
      accept="image/*,application/pdf"
      class="upload-area__input"
      type="file"
      @change="handleFileChange"
    />

    <ButtonContainer class="button-container">
      <template #button-primary>
        <ButtonPrimary
          :disabled="!(fileName || imageUrl) || isLoading"
          :loading="isLoading"
          size="large"
          label="Continuar"
          @click="handleUpload"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Ref } from 'vue-property-decorator';
import { trackContinue, trackView } from './track-events';
import {
  trackEvent,
  trackError
} from '../RegulatoryUpdatePersonalData/track-events';
import { EventKnownProperties } from '@warrenbrasil/web-analytics';
import {
  HeadingDisplay,
  BaseText,
  BaseIcon,
  ButtonIconPrimary,
  InitialAvatarIcon,
  ButtonContainer,
  ButtonPrimary,
  ChipStatus
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { sendAddressDocument } from '@/modules/account/views/RegulatoryUpdate/services';
import { isBFFError } from '@/modules/common/helpers/bff';

@Component({
  components: {
    HeadingDisplay,
    BaseText,
    BaseIcon,
    ButtonIconPrimary,
    InitialAvatarIcon,
    ButtonContainer,
    ButtonPrimary,
    ChipStatus
  }
})
export default class RegulatoryUpdateAddressDocument extends Vue {
  @Prop({ type: Boolean, required: true })
  public showContent!: boolean;

  @Ref('fileInputRef')
  readonly fileInputRef!: HTMLInputElement;

  @Emit()
  private setIsLoading(_isLoading: boolean) {}

  @Emit()
  public goToNextPage() {}

  @Emit()
  private notifyError(_message?: string) {}

  @Emit()
  private changeTracker(
    _newTracker: (remainingProps: EventKnownProperties) => void
  ) {}

  readonly NebraskaColors = NebraskaColors;

  private selectedFile: File | null = null;
  private imageUrl: string | null = null;
  private fileName: string | null = null;
  private isLoading = false;

  mounted() {
    this.setIsLoading(false);
    this.changeTracker(trackEvent);
    trackView();
  }

  private async handleUpload() {
    try {
      this.isLoading = true;

      await sendAddressDocument(this.selectedFile!);

      trackContinue();
      this.goToNextPage();
    } catch (e: unknown) {
      let message =
        'Não foi possível enviar o comprovante de residência. Tente novamente.';

      if (isBFFError(e)) {
        message = e.error.message.formatted;
      }

      trackError(message);
      this.notifyError(message);
    } finally {
      this.isLoading = false;
    }
  }

  private handleFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.selectedFile = target.files[0];
      if (this.selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = e => {
          this.imageUrl = e.target?.result as string;
        };
        reader.readAsDataURL(this.selectedFile);
        this.fileName = null;
      } else if (this.selectedFile.type === 'application/pdf') {
        this.imageUrl = null;
        this.fileName = this.selectedFile.name;
      } else {
        this.imageUrl = null;
        this.fileName = null;
      }
    }
  }

  private removeSelectedFile() {
    this.selectedFile = null;
    this.imageUrl = null;
    this.fileName = null;
    this.fileInputRef.value = '';
  }

  private uploadDocument() {
    this.fileInputRef?.click();
  }
}
</script>

<style lang="less" scoped>
@preview-container-height: 300px;

.base-text {
  margin: @size-spacing-x500 0;
}

.preview-container {
  width: 100%;
  height: @preview-container-height;
  display: flex;
  justify-content: center;
  align-items: center;
  background: @background-tertiary;
  border-radius: @size-radius-x400;
  position: relative;
  margin: @size-spacing-x500 0;

  &__close-button {
    position: absolute;
    top: 24px;
    right: 24px;
    background: @background-overlay;

    :deep(svg) {
      color: @element-over-image;
    }
  }

  &-file {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: @background-secondary;
    border-radius: @size-spacing-x300;
    padding: @size-spacing-x600;
    gap: @size-spacing-x1000;
    max-width: 278px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    &__span {
      color: @element-primary;
      text-overflow: ellipsis;
      width: 222px;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  &-image {
    width: 100%;
    height: @preview-container-height;
    object-fit: cover;
    overflow: hidden;
    border-radius: @size-radius-x400;
    margin: @size-spacing-x500 0;
  }
}

.upload-area {
  border: @size-border-x500 dashed @element-placeholder;
  border-radius: @size-radius-x400;
  background: @background-tertiary;
  width: 100%;
  height: @preview-container-height;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: @size-spacing-x500 0;
  transition: background 0.2s;

  &:hover {
    background: @background-hover;
  }

  &__icon {
    margin-bottom: @size-spacing-x400;
  }

  &__input {
    display: none;
  }
}

.button-container {
  position: fixed;
  left: 0;
  bottom: 0;
  background-color: @background-secondary;
}
</style>
